import { map, some } from "lodash";
import { PeriodRange } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import { setNewTimezone } from "@tootools/date";
import { SnapshotModalState } from "@radarly/shared/toolbar/insight-page/snapshot/state";
import { Clause } from "../clause";
import { getDateFromClause } from "../clause/getter";
import { isDateRangeClause } from "../clause/is";
import { refineClauseWithoutDate } from "../clause/refiner";
import { generatePublicPage } from "../public-pages";
import { InsightPage, InsightPageRecurringSnapshot } from "./model";
import { getPeriodFromInsightPage } from "./period";

export type SnapshotProps = {
  filters: Clause;
  period: PeriodRange;
  page: InsightPage;
};

export type SnapshotStore = SnapshotModalState & {
  filters: Clause;
  period: PeriodRange;
  page: InsightPage;
  title: string;
  recurringSnapshot?: InsightPageRecurringSnapshot;
};

function getPeriodFromClauseAndTimezone(
  clause: Clause,
  timezone: Timezone,
  previousTimezone: Timezone
) {
  if (!some(clause.and, isDateRangeClause)) {
    return null;
  }
  const period = getDateFromClause(clause);
  if (timezone === previousTimezone) {
    return period;
  }
  return {
    from: setNewTimezone(period.from, previousTimezone, timezone),
    to: setNewTimezone(period.to, previousTimezone, timezone),
  };
}

function getPeriodFromPageAndSnapshot(
  page: InsightPage,
  clause: Clause,
  previousTimezone?: Timezone
) {
  if (!page.periodSelector) {
    return null;
  }
  if (!getPeriodFromInsightPage(page)) {
    return null;
  }
  return getPeriodFromClauseAndTimezone(
    clause,
    page.timezone,
    previousTimezone || page.timezone
  );
}

export function getUpdatedPagesFromPageAndSnapshots(
  page: InsightPage,
  snapshots: InsightPageRecurringSnapshot[],
  previousTimezone?: Timezone
): Array<{ id: InsightPageRecurringSnapshot["id"]; page: InsightPage }> {
  return map(snapshots, (snapshot) => {
    const clause: Clause = snapshot.filters || { and: [] };
    const period = getPeriodFromPageAndSnapshot(page, clause, previousTimezone);
    return {
      id: snapshot.id,
      page: generatePublicPage(page, {
        clause: refineClauseWithoutDate(clause),
        period,
        title: snapshot.title,
      }),
    };
  });
}
